// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

import _ from 'lodash'
import moment from 'moment-timezone'
import simpleMoment from 'moment'
import datefns from 'date-fns'

import {
  TIME_MORNING,
  TIME_LUNCH,
  TIME_AFTERNOON,
  DEFAULT_TIME_MORNING,
  DEFAULT_TIME_LUNCH,
  DEFAULT_TIME_AFTERNOON,
} from './../../constants'
import { InvalidRequest } from '../../error'

// https://github.com/moment/moment/issues/5808
// moment.locale('en-au')

const getTzDateFromUTC = (tz, date) =>
  moment(date)
    .tz(tz)
    .format('YYYY-MM-DDTHH:mm:ssZ')
const parseTzDate = (tz, date, format = 'DD/MM/YYYY') =>
  moment.tz(date, format, tz).format()
const formatTimeInTz = (
  date,
  tz = 'Australia/Sydney',
  format = 'YYYY-MM-DD HH:mm:ss',
) =>
  moment(date)
    .tz(tz)
    .format(format)
const isAfter = (date, dateToCompare) => datefns.isAfter(date, dateToCompare)
const isTodayOrFuture = date =>
  datefns.isToday(stripTime(date).toDate(), new Date()) ||
  datefns.isAfter(stripTime(date).toDate(), new Date())

/**
 * return the day of current week
 * @param {string} dayOfWeek refer the day (Mon, Tue, ...)
 * @returns
 */
const getCurrentWeekDay = dayOfWeek => {
  const today = moment()
  return today.day(dayOfWeek).format('YYYY-MM-DD')
}

/**
 * Converts the provided date to an iso date with the related offset for the
 * provided timezone.
 * @param {string} dateStr An iso date string
 * @param {string} tz The timezone, e.g. Europe/Paris or Australia/Sydney
 */
const changeTimezone = (date, tz) => {
  if (typeof date !== 'string') throw new InvalidRequest('Expected a string.')
  if (isValidISODate(date) === false)
    throw new InvalidRequest(
      `Expected an ISO date, instead got '${date}', if you have
      a local date value, consider using 'instantiateLocalDate' first.`.replace(
        /\s+/g,
        ' ',
      ),
    )
  var result = moment(date)
    .tz(tz)
    .format()
  // Moment does not set the offset if the resulting value is UTC. So we have
  // to check for this manually
  if (result.endsWith('Z')) return result.slice(0, -1) + '+00:00'
  return result
}

/**
 * @deprecated Use changeTimezone instead
 * This is an alias of changeTimezone
 */
const setCorrectTimezone = changeTimezone

/**
 * Allows null or a date object
 * @param {Date} date
 * @param {string} timezone
 * @returns {string}
 */
const parseDateForClient = function(date, timezone) {
  if (!date) return null
  if (typeof date === 'string')
    throw new InvalidRequest(
      `Expected date to be date object, instead receieved a
      string.`.replace(/\s+/g, ' '),
    )
  return changeTimezone(date.toISOString(), timezone)
}
/**
 * @deprecated Use parseDateForClient instead
 * This is an alias of parseDateForClient
 */
const parseDateForMobile = parseDateForClient

/**
 * Accepts a local date and/or time string representation and converts it to an
 * ISO with timezone offset string that matches the provided timezone. Useful
 * when you need to keep a clear distinction between specifying a local date
 * and a local time but then need to represent it as an unambigious point in
 * time.
 * @param {string} localDateTimeStr Expects a date in the format 'yyyy-mm-dd'
 * e.g. '2018-04-13' Or if you want to specify time too then
 * 'yyyy-mm-dd hh:mm:ss' e.g. '2018-04-13 14:45:23'. If you ommit the time part
 * then '00:00:00' is implied. You can use formatToLocalDate if you have a date
 * object
 * @param {string} tz The timezone, e.g. Australia/Sydney
 * @returns {string}
 */
const instantiateLocalDate = function(localDateTimeStr, tz) {
  if (isLocalDateStr(localDateTimeStr) === false)
    throw new InvalidRequest(
      `This method only accepts local date strings, not UTC
      strings. '${localDateTimeStr}' was provided.`.replace(/\s+/g, ' '),
    )

  return changeTimezone(moment.tz(localDateTimeStr, tz).toISOString(), tz)
}

/**
 * Instantiates a local date string and returns a Date object, different
 * than `instantiateLocalDate` which returns a string with the correct timezone.
 * This function has come about because doing the following produces two
 * different dates on Safari vs Chrome
 * ```
 * new Date('2019-07-22 15:00:00')
 * ```
 * @param {string} localDateTimeStr
 * @returns {Date}
 */
const browserSafeNewDate = function(localDateTimeStr) {
  if (!isLocalDateStr(localDateTimeStr))
    throw new InvalidRequest('Date is not a local date string.')

  var match = localDateTimeStr.match(
    /(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2}):(\d{2})(.(\d+))?/,
  )
  if (!match) throw new InvalidRequest('Invalid format.')

  var [, year, month, date, hours, minutes, seconds, , millseconds] = match

  return new Date(
    year,
    Number(month) - 1,
    date,
    hours,
    minutes,
    seconds,
    millseconds || 0,
  )
}

/**
 * Takes a date object and returns a local date time string.
 * e.g. '2017-09-10 15:30:00'.
 * @param {Date} date
 * @returns {string}
 */
const formatToLocalDate = function(date) {
  return (
    date.getFullYear() +
    '-' +
    padZero(date.getMonth() + 1) +
    '-' +
    padZero(date.getDate()) +
    ' ' +
    padZero(date.getHours()) +
    ':' +
    padZero(date.getMinutes()) +
    ':' +
    padZero(date.getSeconds())
  )
}

const stripTime = date => {
  date = moment(date)
  date.set('hour', 0)
  date.set('minute', 0)
  date.set('second', 0)
  date.set('millisecond', 0)
  return date
}

const range = (momentStart, endMoment, stepUnit = 'days') => {
  const currentMoment = momentStart.clone()
  const dates = []
  while (!currentMoment.isAfter(endMoment, stepUnit)) {
    dates.push(currentMoment.format())
    currentMoment.add(1, stepUnit)
  }
  return dates
}

const getDayNumber = (tz, date) =>
  formatTimeInTz(date, tz, 'd') == 0 ? 6 : formatTimeInTz(date, tz, 'd') - 1

export const getTimeOffsetFromTimeOfDay = (city, timeOfDay) => {
  const tod = timeOfDay.toUpperCase().trim()
  const times = [
    city.morningCheckingTime,
    city.lunchCheckingTime,
    city.afternoonCheckingTime,
  ]

  const refTime = _.defaults(times, [
    DEFAULT_TIME_MORNING,
    DEFAULT_TIME_LUNCH,
    DEFAULT_TIME_AFTERNOON,
  ])
  let hourOffset = ''

  switch (tod) {
    case TIME_MORNING:
      hourOffset = refTime[0]
      break
    case TIME_LUNCH:
      hourOffset = refTime[1]
      break
    case TIME_AFTERNOON:
      hourOffset = refTime[2]
      break
  }

  const timeArray = hourOffset.split(':')

  return {
    hours: parseInt(timeArray[0], 10),
    minutes: parseInt(timeArray[1], 10),
  }
}

const getTimeFormDateAndTimeOfDay = (
  city,
  date,
  timeOfDay,
  dateFormat = 'DD/MM/YYYY',
) => {
  const utcDate = parseTzDate(city.timezone, date, dateFormat)
  const { hours, minutes } = getTimeOffsetFromTimeOfDay(city, timeOfDay)
  return moment(utcDate)
    .add({ hours, minutes })
    .utc()
    .format()
}

const weekDayDetails = (tz, date) => {
  const dayOfWeekInTz = getDayNumber(tz, date)
  return {
    weekDays: dayOfWeekInTz < 5 ? _.range(dayOfWeekInTz, 5) : [],
    weekendDays: dayOfWeekInTz < 5 ? _.range(5, 7) : _.range(dayOfWeekInTz, 7),
    get weekDayCount() {
      return this.weekDays.length
    },
    get weekendDayCount() {
      return this.weekendDays.length
    },
    get totalCount() {
      return this.weekDayCount + this.weekendDayCount
    },
  }
}

const paymentDueDate = () => {
  throw new Error(
    `This method has been deprecated. 
    Use getPaymentDueDate instead.`.replace(/\s+/g, ' '),
  )
}

/**
 * Gets the date at Midnight for the following Friday after 2 weeks.
 * e.g. If I do a mission on Thursday of week 1 I will get paid the Friday of
 * week 3.
 * @param {Date} date
 * @returns {Date}
 */
const getPaymentDueDate = function(date) {
  date = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  )
  date.setDate(date.getDate() + 14 + (5 - date.getDay()))
  return date
}

/**
 * Gets the point in time at the end of the week.
 * So if you pass in Tuesday at 3PM this will return Sunday at 11PM and 59
 * minutes.
 * @param {Date} date
 * @return {Date}
 */
const getEndOfWeek = function(date) {
  var dayDiff = (date.getDay() - 1 + 7) % 7
  var sunday = new Date(date)
  sunday.setHours(23)
  sunday.setMinutes(59)
  sunday.setSeconds(59)
  sunday.setMilliseconds(999)
  sunday.setDate(sunday.getDate() + (6 - dayDiff))
  return sunday
}

const startOfWeek = (tz, date) => {
  const dateInTz = formatTimeInTz(date, tz)
  const weekStart = datefns.format(
    datefns.startOfWeek(dateInTz, { weekStartsOn: 1 }),
    'DD/MM/YYYY',
  )
  return moment(parseTzDate(tz, weekStart))
    .tz('UTC')
    .format()
}

/**
 * @Deprecated use simplerGetTimeOfDayFromDate
 * Determine which timeOfDay is, based on timezone and city preferences
 *
 * @param {String} date
 * @param {CityModel} city
 */
const getTimeOfDayFromDate = (date, city) => {
  const convertToNumber = h => {
    const parts = h.split(':')
    return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
  }

  const lunchLimit = convertToNumber(
    city.lunchCheckingTime || DEFAULT_TIME_LUNCH,
  )
  const afternoonLimit = convertToNumber(
    city.afternoonCheckingTime || DEFAULT_TIME_AFTERNOON,
  )
  const hour = convertToNumber(moment.tz(date, city.timezone).format('HH:mm'))

  if (hour < lunchLimit) return TIME_MORNING
  if (hour < afternoonLimit) return TIME_LUNCH
  return TIME_AFTERNOON
}

/**
 * @typedef CityTimeOfDaySettings
 * @property {string} lunchCheckInTime
 * @property {string} afternoonCheckInTime
 * Get either MORNING, LUNCH or AFTERNOON from a date and the city settings
 * @param {string} localDateStr
 * @param {CityTimeOfDaySettings} citySettings
 */
const simplerGetTimeOfDayFromDate = (localDateStr, city) => {
  if (!isLocalDateStr(localDateStr))
    throw new InvalidRequest('Expected a local data string.')
  if (!city.lunchCheckInTime)
    throw new InvalidRequest("Missing city prop 'lunchCheckInTime'.")
  if (!city.afternoonCheckInTime)
    throw new InvalidRequest("Missing city prop 'lunchCheckInTime'.")

  var time = getTimeFromLocalDateStr(localDateStr)
  if (time < city.lunchCheckInTime) return 'MORNING'
  else if (time >= city.afternoonCheckInTime) return 'AFTERNOON'
  else return 'LUNCH'
}

/**
 * Get the time part of a local date string.
 * eg. '2019-06-13 10:20:33.234' will return '10:20:33.234' and
 * '2019-06-13 10:20:33' will return '10:20:33'
 * @param {string} dateStr
 * @returns {string}
 */
const getTimeFromLocalDateStr = function(dateStr) {
  if (!isLocalDateStr(dateStr))
    throw new InvalidRequest('Expected a local data string.')

  return dateStr.slice(11)
}

/**
 * @deprecated Use getPotentialMissionStartDate instead
 * This gets a potential mission start date, using the params as an anchor
 * @param {City} city Grabs timezone information from the city
 * @param {string} referenceDate An iso date string. Will figure out what week
 * this relates to based on this date
 * @param {number} dayIndex The day index of when to return the date for
 * i.e. 2 would be Wednesday and 4 would be Friday.
 * @param {string} timeOfDay The time of day to return the mission for.
 * Unfortuntely does not follow the JS standard of Sunday being 0, and instead
 * Monday is 0
 */
const getDateStartForCity = (city, referenceDate, dayIndex, timeOfDay) => {
  const { hours, minutes } = getTimeOffsetFromTimeOfDay(city, timeOfDay)

  var dateInWeek = changeTimezone(referenceDate, city.timezone)
  return getPotentialMissionStartDate(
    dateInWeek,
    dayIndex,
    hours * 60 + minutes,
  )
}

/**
 * @param {string} dateInWeek This can be any date that occurs during the week
 * we are trying to get a mission start date for
 * @param {string} newDayIndex This will return a date where the day corresponds
 * to this value. It's 0 based on Monday. Sunday is 6
 */
const getPotentialMissionStartDate = (
  dateInWeek,
  newDayIndex,
  timeOfDayInMinutes,
) => {
  if (isValidISODate(dateInWeek) === false)
    throw new InvalidRequest(`Invalid dateInWeek '${dateInWeek}'.`)

  var localDateStr = dropTimeZoneFromDate(dateInWeek)
  // Do all the date manipulation on a local date
  var date = new Date(localDateStr)
  var dayDiff = newDayIndex - ((date.getDay() - 1 + 7) % 7)
  date.setDate(date.getDate() + dayDiff)
  date.setHours(0)
  date.setMinutes(timeOfDayInMinutes)
  // Remove the offset of this local date
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset())
  // So it is now local to UTC and the correct time

  // Then we must set the local date relative back to its timezone
  var offset = getTimeZoneFromDate(dateInWeek)
  var parts = offset.split(':')
  date.setHours(date.getHours() - Number(parts[0]))
  date.setMinutes(date.getMinutes() - Number(parts[1]))
  return date.toISOString()
}

/**
 * Creates a new date, adds minutes to it and then returns the new date.
 * @param {Date} date
 * @param {number} minutes
 */
const addMinutesToDate = (date, minutes) => {
  if (Object.prototype.toString.call(date) !== '[object Date]')
    throw new InvalidRequest(`Expected a date object, received ${date}`)
  var clone = new Date(date)
  clone.setMinutes(clone.getMinutes() + minutes)
  return clone
}

const isTimeRangeOverlaping = (
  dayA,
  dayB,
  refStartA,
  refStopA,
  startB,
  stopB,
) => {
  if (dayA !== dayB) return false
  return isTimeRangeConflicting(refStartA, refStopA, startB, stopB)
}

const getDayTimeRange = (timezone, date = moment.now()) => {
  const start = moment(date).tz(timezone)
  const stop = start.clone()
  return [
    start
      .set({ hours: 0, minutes: 0, seconds: 0 })
      .utc()
      .format(),
    stop
      .set({ hours: 23, minutes: 59, seconds: 59 })
      .utc()
      .format(),
  ]
}

const isTimeRangeConflicting = (refStartA, refStopA, startB, stopB) => {
  const fakerefStartA = new Date(`2000-01-01 ${refStartA}`)
  const fakerefStopA = new Date(`2000-01-01 ${refStopA}`)
  const fakeStartB = new Date(`2000-01-01 ${startB}`)
  const fakeStopB = new Date(`2000-01-01 ${stopB}`)

  const stopInRange =
    datefns.isAfter(fakeStopB, fakerefStartA) &&
    datefns.isBefore(fakeStopB, fakerefStopA)
  const startInRange =
    datefns.isAfter(fakeStartB, fakerefStartA) &&
    datefns.isBefore(fakeStartB, fakerefStopA)

  return stopInRange || startInRange
}

const startTimeBeforeStop = (start, stop) =>
  datefns.isBefore(
    new Date(`2000-01-01 ${start}`),
    new Date(`2000-01-01 ${stop}`),
  )

const weeksFromDate = date => {
  const diff = moment().second() - moment(date).second()
  const duration = moment.duration(diff, 'seconds')
  return duration.asWeeks()
}
const maxDate = (dates = []) => datefns.max(dates)
const minDate = (dates = []) => datefns.min(dates)

// NOTE: This seems to be 0 based on Sunday
// @deprecated Use getISOWeekdayIndex()
const getWeekDayIndex = dayString => moment(dayString, 'dddd').day()
//const getEndDateFromWeek = (isoWeek, year, timezone) => moment(`${isoWeek}-${year}`, 'W-YYYY').tz(timezone).endOf('week').format()

/**
 * Adapted from https://stackoverflow.com/a/16591175/837649
 */
function getStartDateFromWeek(w, y, timezone) {
  return moment
    .tz({ 'year': y }, timezone)
    .week(w)
    .startOf('isoWeek')
    .toISOString()
}

/**
 * Taken from https://stackoverflow.com/a/16591175/837649
 * @returns {Date}
 */
function getDateOfISOWeek(w, y) {
  var simple = new Date(y, 0, 1 + (w - 1) * 7)
  var dow = simple.getDay()
  var ISOweekStart = simple
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1)
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay())
  return ISOweekStart
}

function getEndDateFromWeek(w, y, timezone) {
  // We'll cheat and leverage the other function
  var result = getStartDateFromWeek(parseInt(w, 10) + 1, y, timezone)
  var date = new Date(result)
  date.setMilliseconds(date.getMilliseconds() - 1)
  return date.toISOString()
}

/**
 * Get a readable string describing a mission date time change relative to now.
 * All dates should be UTC
 * @param {string} tz The timezone
 * @param {string} now
 * @param {string} from
 * @param {string} to
 */
const getReadableMissionDateTimeChange = (tz, now, from, to) => {
  // Convert all dates to local date objects relative to their timezones
  now = new Date(dropTimeZoneFromDate(getTzDateFromUTC(tz, now)))
  from = new Date(dropTimeZoneFromDate(getTzDateFromUTC(tz, from)))
  to = new Date(dropTimeZoneFromDate(getTzDateFromUTC(tz, to)))

  if (to < now) throw new InvalidRequest("'to' date can not be in the past")

  return (
    `Your mission
    ${getFromDateString(now, from, to)}
    has been moved
    ${getToDateString(now, from, to)}
  `
      .replace(/\r?\n|\r/g, '')
      // Remove any extra spaces
      .replace(/\s+/g, ' ')
      .trim()
  )

  /**
   * Gets the from part of the string
   */
  function getFromDateString(now, from) {
    var fromDateIsToday = from.getDate() == now.getDate()

    var fromString = getMissionDateTimeString(now, from)

    if (fromString.startsWith('tomorrow') || fromString.startsWith('today'))
      return fromString

    return (fromDateIsToday ? 'at ' : 'on ') + fromString
  }

  /**
   * Gets the to part of the string
   */
  function getToDateString(now, from, to) {
    var fromDateIsToday = from.getDate() == now.getDate(),
      toDateIsToday = to.getDate() == now.getDate()

    var toString = getMissionDateTimeString(
      now,
      to,
      !fromDateIsToday && toDateIsToday,
    )

    return `to ${toString}`
  }
}

/**
 * Gets the date relative to now, so if the date is today then will return
 * 'today'. Or 'tomorrow' else 'Mon Jul 1st', 'Sat Apr 2nd', etc.
 * @param {Date} now
 * @param {Date} date
 * @param {boolean} beExplicitAboutToday Instead of just returning the time if
 * its today this will say the time and "today"
 */
const getMissionDateTimeString = (now, date, beExplicitAboutToday = false) => {
  if (now.getDate() == date.getDate()) {
    // If it's the same day then only return the time
    return getShortTime(date) + (beExplicitAboutToday ? ' today' : '')
  }

  // Let the Date object do the work
  var nowClone = new Date(now.valueOf())
  nowClone.setDate(now.getDate() + 1)

  if (nowClone.getDate() == date.getDate())
    return 'tomorrow at ' + getShortTime(date)

  // If its less than 7 days in the future then just say the week
  // Again let the Date object do the work
  nowClone = new Date(now.valueOf())
  nowClone.setDate(nowClone.getDate() + 7)
  if (nowClone > date) {
    return getLongWeekDay(date.getDay()) + ' at ' + getShortTime(date)
  }

  return getReadbleDateTimeString(date)
}

/**
 * Gets a readable date string. e.g. Tue Jul 17th at 1PM
 * @param {Date} date
 * @returns {string}
 */
const getReadbleDateTimeString = date => {
  if (!date) return '-'
  if (typeof date === 'string') date = new Date(date)
  return getReadbleDateString(date) + ' at ' + getShortTime(date)
}

/**
 * Gets a readable date string. e.g. Tue Jul 17th
 * @param {Date} date
 * @returns {string}
 */
const getReadbleDateString = date => {
  return (
    getShortWeekDay(date.getDay()) +
    ' ' +
    getShortMonth(date.getMonth()) +
    ' ' +
    getDescriptiveDate(date.getDate())
  )
}

/**
 * Gets a readable date string v2. e.g. Jul 17, 2019
 * // TODO: Not happy about having multiple versions of displaying a date,
 * // perhaps can be standardised in the future
 * @param {Date} date
 * @returns {string}
 */
const getReadbleDateStringV2 = date => {
  return (
    getShortMonth(date.getMonth()) +
    ' ' +
    date.getDate() +
    ', ' +
    date.getFullYear()
  )
}

/**
 * Gets the short week name of a date e.g. Tue, Wed, Thu
 * @param {number} day 0 to 6 (Sunday is 0, same as what's returned from getDay())
 * @returns {string}
 */
const getShortWeekDay = day => {
  switch (day) {
    case 0:
      return 'Sun'
    case 1:
      return 'Mon'
    case 2:
      return 'Tue'
    case 3:
      return 'Wed'
    case 4:
      return 'Thu'
    case 5:
      return 'Fri'
    case 6:
      return 'Sat'
    default:
      throw new InvalidRequest('Invalid weekday')
  }
}

/**
 * Gets the long week name of a date e.g. Tuesday, Wednesday, Thursday
 * @param {number} day 0 to 6
 * @returns {string}
 */
const getLongWeekDay = day => {
  switch (day) {
    case 0:
      return 'Sunday'
    case 1:
      return 'Monday'
    case 2:
      return 'Tuesday'
    case 3:
      return 'Wednesday'
    case 4:
      return 'Thursday'
    case 5:
      return 'Friday'
    case 6:
      return 'Saturday'
    default:
      throw new InvalidRequest('Invalid weekday')
  }
}

/**
 * Gets the short month name of a date e.e. Jan, Feb, Mar
 * @param {number} 0 to 11
 * @returns {string}
 */
export const getShortMonth = monthInt => {
  switch (monthInt) {
    case 0:
      return 'Jan'
    case 1:
      return 'Feb'
    case 2:
      return 'Mar'
    case 3:
      return 'Apr'
    case 4:
      return 'May'
    case 5:
      return 'Jun'
    case 6:
      return 'Jul'
    case 7:
      return 'Aug'
    case 8:
      return 'Sep'
    case 9:
      return 'Oct'
    case 10:
      return 'Nov'
    case 11:
      return 'Dec'
    default:
      throw new InvalidRequest('Invalid month')
  }
}

/**
 * Gets the date with 'st', 'th', etc appended.
 * @param {number} 1 to 31
 * @returns {string}
 */
export const getDescriptiveDate = date => {
  switch (date) {
    case 1:
    case 21:
    case 31:
      return date + 'st'
    case 2:
    case 22:
      return date + 'nd'
    case 3:
    case 23:
      return date + 'rd'
    case 4:
    case 5:
    case 6:
    case 7:
    case 8:
    case 9:
    case 10:
    case 11:
    case 12:
    case 13:
    case 14:
    case 15:
    case 16:
    case 17:
    case 18:
    case 19:
    case 20:
    case 24:
    case 25:
    case 26:
    case 27:
    case 28:
    case 29:
    case 30:
      return date + 'th'
    default:
      throw new InvalidRequest('Invalid date')
  }
}

/**
 * Get a date's time in a readable format eg. 11AM, 1PM
 * If its noon it will return 'Noon' and 'Midnight' for 12AM so avoid
 * ambiguity between AM and PM
 * @param {date} date
 * @param {boolean} unambiguous12hour Defaults to true, when specified shows
 * 12:00 as Noon and 00:00 as midnight else sticks to 12:00 and 00:00
 * @returns {string}
 */
const getShortTime = (date, unambiguous12hour = true) => {
  var hours = date.getHours()
  var minutes = date.getMinutes()
  var meridiem = hours / 12 >= 1 ? 'PM' : 'AM'
  if (minutes == 0) {
    if (unambiguous12hour) {
      if (hours == 0) return 'Midnight'
      if (hours == 12) return 'Noon'
    }
    if (meridiem == 'PM') {
      hours = hours - 12
      return hours + 'PM'
    }
    return hours + 'AM'
  }
  // Format the minutes to 2dp
  minutes += ''
  if (minutes.length == 1) minutes = '0' + minutes

  return hours + '.' + minutes + meridiem
}

/**
 * Get 24 hour time from a data
 * @param {Date} date
 * @returns {string}
 */
const get24HourTime = function(date) {
  var hours = date.getHours()
  var minutes = date.getMinutes()
  return padZero(hours) + ':' + padZero(minutes)
}

/**
 * Given a iso date with a timezone, this will simply drop the timezone off the
 * end so that if you instanciate it with `new Date(dateStr)` it won't do any
 * timezone manipulation and you'll have a date object that's local to that
 * timezone.
 * @returns {string}
 */
function dropTimeZoneFromDate(isoDateStr) {
  if (!isoDateStr)
    throw new InvalidRequest(`Received a falsy value: ${isoDateStr}`)

  var match = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d+)?([+-]{1}\d{2}:\d{2})/.test(
    isoDateStr,
  )

  if (!match)
    throw new InvalidRequest(
      `Expected isoDateStr '${isoDateStr}' to have a timezone offset.`,
    )

  return isoDateStr.slice(0, -6)
}

/**
 * Simlar to dropTimeZoneFromDate but instead returns the offset
 * @returns {string}
 */
function getTimeZoneFromDate(isoDateStr) {
  var match = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d+)?([+-]{1}\d{2}:\d{2})/.test(
    isoDateStr,
  )

  if (!match)
    throw new InvalidRequest(
      'Invalid date format. Did not contain timezone offset.',
    )

  return isoDateStr.slice(-5)
}

/**
 * Get the week number e.g first new week in Jan is 1, last week in December
 * is 52.
 * Taken and modified from https://stackoverflow.com/a/6117889/837649
 * @param {Date} d
 * @returns {number} 1 to 53
 */
function getWeekNumber(d) {
  return getWeekNumberAndYear(d).weekNumber
}

/**
 * Get the week number e.g first new week in Jan is 1, last week in December
 * is 52. And return the year e.g. 2019
 * Taken and modified from https://stackoverflow.com/a/6117889/837649
 * @param {Date} d
 * @returns {number} 1 to 53
 */
function getWeekNumberAndYear(rawDate) {
  const dateObject = new Date(rawDate)

  return {
    weekNumber: moment(dateObject).isoWeek(),
    year: dateObject.getFullYear(),
  }
}

function getIsoWeekNumberAndIsoYear(rawDate) {
  const date = simpleMoment(rawDate)

  return {
    weekNumber: date.isoWeek(),
    year: date.isoWeekYear(),
  }
}

/**
 * Converts 1 to W01 and 30 to W30
 * @param {num}
 * @returns {string}
 */
function formatWeekNumber(num) {
  num = num + ''
  if (num.length == 1) num = '0' + num

  return 'W' + num
}

/**
 * Converts to an ISO string with timezone.
 * See: https://stackoverflow.com/a/17415677/837649
 */
const toISOStringWithTZ = function(date) {
  var tzo = -date.getTimezoneOffset(),
    dif = tzo >= 0 ? '+' : '-',
    pad = function(num) {
      var norm = Math.floor(Math.abs(num))
      return (norm < 10 ? '0' : '') + norm
    }
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds()) +
    dif +
    pad(tzo / 60) +
    ':' +
    pad(tzo % 60)
  )
}

/*const getReadableTimestamp = date => {
  return `
    ${date.getFullYear()}-${padZero(date.getMonth() + 1)}-${padZero(get
}*/

/**
 * Pads a string with a leading zero if its only one character long
 * @param {string|number} val
 * @returns {string}
 */
const padZero = val => {
  val += ''
  if (val.length === 0) return '00'
  else if (val.length === 1) return '0' + val
  else return val
}

// return diff in hrs
const hrsDiffCalc = (DateA, DateB) => {
  const firstDate = moment(DateA)
  const secondDate = moment(DateB)
  const duration = moment.duration(firstDate.diff(secondDate))
  const hours = duration.asHours()
  return Math.abs(hours)
}

/**
 * Check if a date is a valid ISO date
 * @param {string} dateStr
 * @return {boolean}
 */
const isValidISODate = function(dateStr) {
  var match = dateStr.match(
    /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(.\d+)?(([+-]{1}\d{1,2}:\d{2})|Z){1}/,
  )
  return !!match
}

/**
 * This function converts a date to have a new timezone offset. For example
 * you may want to convert a UTC date with no offset to a local Sydney date
 * with the correct timezone offset for Sydney. Both the date passed in and
 * returned will mean exactly the same point in time but the date returned
 * will have a different offset.
 * @param {string} dateStr An ISO date
 * @param {string} timezoneOffset The offset e.g. +05:00 or -11:00
 * @returns {string}
 */
const convertDateISODateForTimezone = function(dateStr, timezoneOffset) {
  var match = dateStr.match(
    /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(.\d+)?([+-]{1}\d{2}:\d{2})/,
  )
  if (!match) throw new InvalidRequest('Invalid ISO date.')

  var [
    ,
    year,
    month,
    date,
    hours,
    minutes,
    seconds,
    millseconds,
    existingTimezoneOffset,
  ] = match

  // Normalise the date via the existing timezone offset
  var parts = existingTimezoneOffset.split(':')
  var operator = existingTimezoneOffset.slice(0, 1)
  hours = Number(hours) - Number(parts[0])
  minutes = Number(minutes) - Number(operator + parts[1])

  // Modify by the new timezone
  parts = timezoneOffset.split(':')
  operator = timezoneOffset.slice(0, 1)
  hours = hours + Number(parts[0])
  minutes = minutes + Number(operator + parts[1])

  // Let's use a date object to figure out how changing the hours and minutes
  // will adjust the other values
  var tempDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(date),
    Number(hours),
    Number(minutes),
  )
  year = tempDate.getFullYear() + ''
  date = tempDate.getDate() + ''
  month = tempDate.getMonth() + 1 + ''
  hours = tempDate.getHours() + ''
  minutes = tempDate.getMinutes() + ''

  return (
    year +
    '-' +
    padZero(month) +
    '-' +
    padZero(date) +
    'T' +
    padZero(hours) +
    ':' +
    padZero(minutes) +
    ':' +
    seconds +
    (millseconds ? millseconds : '') +
    timezoneOffset
  )
}

/**
 * Takes a date string that contains the timezone offset and displays it
 * as a short date string local to that timezone.
 * @param {string|null} dateStr
 * @retuns {string}
 */
const formatToLocalShortDate = function(dateStr) {
  if (!dateStr) return '-'
  var localStr = dropTimeZoneFromDate(dateStr)
  var localDate = new Date(localStr)
  return localDate.toLocaleDateString()
}

/**
 * Throws if the provided dateStr is not of the format 'YYYY-MM-DD',
 * or if parsing the date with moment fails (i.e. '2020-04-33').
 *
 * @param {string} dateStr
 */
const assertIsYmdString = dateStr => {
  if (typeof dateStr !== 'string') {
    throw new InvalidRequest(
      `YMD date string is not a string, but is a: ${typeof dateStr}`,
    )
  }
  if (!dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    throw new InvalidRequest(
      `YMD date string does not match format 'YYYY-MM-DD': ${dateStr}`,
    )
  }
  if (!moment(dateStr).isValid()) {
    throw new InvalidRequest(
      `YMD date string considered invalid by moment: ${dateStr}`,
    )
  }
}

/**
 * Returns the number of weeks between two dates
 * moment starts from week 0, hence the +1 in the end
 **/
const getNumberOfWeeksFrom = (date, date2 = new Date()) =>
  moment.duration(moment(date2).diff(moment(date))).weeks() + 1

/**
 * Determine if a date string is a local date string.
 * @param {string} dateStr
 * @returns {boolean}
 */
const isLocalDateStr = function(dateStr) {
  // This is kind of a cheat. But if we can instantiate the date for two
  // different timezones and return the same result we know it was an
  // unambigious date that was passed in and not local. We want to throw
  // in these scenarios to catch potential mistakes in coding
  var parisStr = moment.tz(dateStr, 'Europe/Paris').toISOString(),
    sydneyStr = moment.tz(dateStr, 'Australia/Sydney').toISOString()
  return parisStr !== sydneyStr
}

/**
 * The same as the native Date.prototype.toLocaleDateString for when you are
 * in a locale that puts the date before the month. e.g. 17/04/2019
 * @param {Date} date
 * @returns {string}
 */
const toLocaleNonAmericanDateString = function(date) {
  return `${padZero(date.getDate())}/${padZero(date.getMonth() + 1)}/
    ${date.getFullYear()}`.replace(/\s+/g, '')
}

/**
 * Given a date in the format DD/MM/YYYY e.g. 13/06/2019 it will return a
 * date object.
 * @param {string} dateStr
 */
const formatShortLocalDateStringToDate = function(dateStr) {
  if (!/\d{1,2}\/\d{1,2}\/\d{2,4}/.test(dateStr))
    throw new InvalidRequest(
      `Invalid date string, expected a date in the format 
      DD/MM/YYYY.`.replace(/\s+/g, ' '),
    )

  var [date, month, year] = dateStr.split('/')
  if (year.length == 2) year = '20' + year

  return new Date(Number(year), Number(month) - 1, Number(date))
}

const getNextNthDay = (dayOfWeek, now) => {
  // now will default to undefined if not provided --> now
  // it will be provided for unit tests
  const today = moment(now)

  let nextNthDay = today.day(dayOfWeek)
  if (nextNthDay.isBefore(moment(now))) {
    nextNthDay = nextNthDay.add(7, 'days')
  }
  return nextNthDay.format('YYYY-MM-DD')
}

/**
 * Returns the day of the week starting with 1 on Monday through to 7 on
 * Sunday.
 * @param {Date} date
 * @returns {number}
 */
const getISOWeekdayIndex = date => {
  return ((date.getDay() - 1 + 7) % 7) + 1
}

/**
 * Returns date ignoring timezone or null for invalid date
 * Convenience function that will return null for invalid date
 *  instead of throwing error.
 * @param {string} isoDateStr date in ISO string format
 * @returns {Date|null}
 */
const formatIsoToLocalDateSafe = isoDateStr => {
  try {
    return typeof isoDateStr === 'string'
      ? browserSafeNewDate(dropTimeZoneFromDate(isoDateStr))
      : null
  } catch (e) {
    return null
  }
}

/**
 * Add weeks to a date and return a new date
 * @param {Date} date
 * @param {number} weeks
 * @returns {Date}
 */
const addWeeks = function(date, weeks) {
  var temp = new Date(date)
  temp.setDate(temp.getDate() + weeks * 7)
  return temp
}

/**
 * Gets week number from provided date in provided timezone
 * @param {string|Date} dateTime
 * @param {string} tz timezone
 * @returns {string} {'1'-'53'}
 */
const getWeekNumberInTz = (dateTime, tz = null) =>
  moment.tz(dateTime, tz).format('w')

/**
 * Gets week number and year from provided date in provided timezone
 * @param {string|Date} dateTime
 * @param {string} tz timezone
 * @returns {Object} res
 * @returns {string} res.weekNumber {1-53}
 * @returns {string} res.year
 */
const getWeekNumberAndYearInTz = (dateTime, tz = null) => ({
  weekNumber: moment.tz(dateTime, tz).format('W'),
  year: moment.tz(dateTime, tz).format('YYYY'),
})

/**
 * @param {String} dateStr Date ISO string
 * @returns {string} Format: DD/MM/YYYY HH:mm (25/03/2020 12:55)
 */
const getReadableDateTimeStringFromISO = dateStr => {
  const [date, time] = dateStr.split('T')
  const [year, month, day] = date.split('-')
  const [hours, minutes] = time.split(':')
  return `${day}/${month}/${year} ${hours}:${minutes}`
}
/** Format minutes to hours with minutes
 * @param {number} minutes
 *
 * @returns {string} Format: 5 h 19 m
 */
const timeConvert = n => {
  const num = n
  const hours = num / 60
  const rhours = Math.floor(hours)
  const minutes = (hours - rhours) * 60
  const rminutes = Math.round(minutes)
  return rhours + ' h ' + rminutes + ' m'
}

/** Format minutes to hours with minutes
 * @param {object} city
 * @param {string} timeOfDay
 * @returns {string} Format:{checkInTimeBefore:30, checkInTimeAfter:30}
 */
const getTimeOfDayCheckInRanges = (city, timeOfDay) => {
  let checkInTimeBefore = 0
  let checkInTimeAfter = 0

  if (timeOfDay === TIME_MORNING) {
    checkInTimeBefore = city.morningCheckInTimeBefore
    checkInTimeAfter = city.morningCheckInTimeAfter
  }

  if (timeOfDay === TIME_LUNCH) {
    checkInTimeBefore = city.lunchCheckInTimeBefore
    checkInTimeAfter = city.lunchCheckInTimeAfter
  }

  if (timeOfDay === TIME_AFTERNOON) {
    checkInTimeBefore = city.afternoonCheckInTimeBefore
    checkInTimeAfter = city.afternoonCheckInTimeAfter
  }
  return { checkInTimeBefore, checkInTimeAfter }
}

const convertDateToTimezone = (date, timezone) =>
  instantiateLocalDate(dropTimeZoneFromDate(moment(date).format()), timezone)

const getDateStartAndEndWeek = (date, timezone) => {
  const week = getWeekNumber(date) + 1
  const year = date.getFullYear()
  const startDate = getStartDateFromWeek(week, year, timezone)
  const endDate = getEndDateFromWeek(week, year, timezone)

  return { startDate, endDate }
}

export {
  getStartDateFromWeek,
  getDateOfISOWeek,
  getEndDateFromWeek,
  getTzDateFromUTC,
  getTimeOfDayFromDate,
  simplerGetTimeOfDayFromDate,
  startOfWeek,
  parseTzDate,
  formatTimeInTz,
  isAfter,
  isTodayOrFuture,
  weekDayDetails,
  getDayNumber,
  getDateStartForCity,
  getPotentialMissionStartDate,
  addMinutesToDate,
  getTimeFormDateAndTimeOfDay,
  isTimeRangeConflicting,
  isTimeRangeOverlaping,
  startTimeBeforeStop,
  weeksFromDate,
  maxDate,
  minDate,
  getDayTimeRange,
  range,
  getWeekDayIndex,
  paymentDueDate,
  getPaymentDueDate,
  getReadableMissionDateTimeChange,
  getReadbleDateString,
  getReadbleDateStringV2,
  getMissionDateTimeString,
  getShortTime,
  dropTimeZoneFromDate,
  getWeekNumber,
  getWeekNumberAndYear,
  formatWeekNumber,
  getReadbleDateTimeString,
  padZero,
  hrsDiffCalc,
  convertDateISODateForTimezone,
  parseDateForClient,
  setCorrectTimezone,
  changeTimezone,
  parseDateForMobile,
  isValidISODate,
  getTimeZoneFromDate,
  instantiateLocalDate,
  formatToLocalShortDate,
  getShortWeekDay,
  getLongWeekDay,
  getEndOfWeek,
  formatToLocalDate,
  get24HourTime,
  assertIsYmdString,
  getNumberOfWeeksFrom,
  isLocalDateStr,
  toLocaleNonAmericanDateString,
  toISOStringWithTZ,
  formatShortLocalDateStringToDate,
  getNextNthDay,
  getISOWeekdayIndex,
  browserSafeNewDate,
  formatIsoToLocalDateSafe,
  addWeeks,
  getWeekNumberInTz,
  getWeekNumberAndYearInTz,
  getReadableDateTimeStringFromISO,
  timeConvert,
  getTimeOfDayCheckInRanges,
  convertDateToTimezone,
  getDateStartAndEndWeek,
  getCurrentWeekDay,
  getIsoWeekNumberAndIsoYear,
}
